import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CompetitorFinderResponse,
  InputKeywordPerformance,
  CompetitorFinderRow,
  CompetitorFinderRunListItem,
  CompetitorFinderService
} from '../../services/competitor-finder';
import { CrawlEstimate, CrawlService, CrawlStatus } from '../../services/crawl';

@Component({
  selector: 'app-competitor-finder',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './competitor-finder.html',
  styleUrl: './competitor-finder.css'
})
export class CompetitorFinderComponent implements OnInit, OnDestroy {
  domain = '';
  limit = 20;
  maxDomainSitemapPages: number | null = null;
  assistedTopicsLimit = 5;
  loading = false;
  runProgress = 0;
  runProgressLabel = '';
  runProgressDetail = '';
  private runProgressInterval: any = null;
  private runBackendProgressInterval: any = null;
  private activeRunId: string | null = null;
  private runStartedAt: number | null = null;
  previewLoading = false;
  error: string | null = null;
  rows: CompetitorFinderRow[] = [];
  assistedRows: CompetitorFinderRow[] = [];
  assistedAvailability: boolean | null = null;
  searchPlanKeywords: string[] = [];
  searchPlanTopics: string[] = [];
  selectedAssistedTopics: string[] = [];
  availableAssistedTopics: string[] = [];
  draggedTopic: string | null = null;
  pendingSwapTopic: { topic: string; source: 'selected' | 'available' } | null = null;
  estimatedCosts: { total: number; competitorsDomain: number; serpCompetitors: number; rankedKeywords: number; domainIntersection: number } | null = null;
  inputKeywordPerformance: InputKeywordPerformance | null = null;

  metadata: {
    dataSource: 'database' | 'api' | null;
    lastUpdated: string | null;
    maxDomainSitemapPages: number | null;
    pageAnalysisAssistedAvailable: boolean | null;
    cacheMissReason: string | null;
  } = {
    dataSource: null,
    lastUpdated: null,
    maxDomainSitemapPages: null,
    pageAnalysisAssistedAvailable: null,
    cacheMissReason: null
  };

  costBreakdown: { competitorsDomain: number; domainIntersection?: number; total: number } | null = null;
  stats: { totalCandidates: number; totalRows: number; excludedByDenylist?: number; excludedBySitemapSize?: number; sitemapCheckTimeouts?: number } | null = null;
  assistedStats: { totalCandidates: number; totalRows: number } | null = null;
  savedRuns: CompetitorFinderRunListItem[] = [];
  savedRunsLoading = false;
  selectedSavedRunId = '';
  loadingSavedRun = false;
  currentRunRecordId = '';

  crawlStatuses = new Map<string, CrawlStatus>();
  crawlStatusLoading = new Map<string, boolean>();
  crawlStatusError = new Map<string, string | null>();
  crawlEstimates = new Map<string, CrawlEstimate>();
  crawlEstimateLoading = new Map<string, boolean>();
  crawlEstimateError = new Map<string, string | null>();
  crawlStarting = new Map<string, boolean>();
  removingRows = new Map<string, boolean>();
  crawlPollingIntervals = new Map<string, any>();

  constructor(
    private competitorFinderService: CompetitorFinderService,
    private crawlService: CrawlService
  ) {}

  ngOnInit(): void {
    this.loadSavedRuns();
  }

  ngOnDestroy(): void {
    this.crawlPollingIntervals.forEach(interval => clearInterval(interval));
    this.crawlPollingIntervals.clear();
    if (this.runProgressInterval) clearInterval(this.runProgressInterval);
    if (this.runBackendProgressInterval) clearInterval(this.runBackendProgressInterval);
  }

  private normalizeDomain(value: string): string {
    return value
      .toLowerCase()
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0];
  }

  async run(forceRefresh = false): Promise<void> {
    const normalizedDomain = this.normalizeDomain(this.domain);
    if (!normalizedDomain) {
      this.error = 'Please enter a valid domain.';
      return;
    }

    this.loading = true;
    const runId = `cf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    this.activeRunId = runId;
    this.startRunProgress(runId);
    this.error = null;
    this.rows = [];
    this.assistedRows = [];
    this.metadata = {
      dataSource: null,
      lastUpdated: null,
      maxDomainSitemapPages: null,
      pageAnalysisAssistedAvailable: null,
      cacheMissReason: null
    };
    this.costBreakdown = null;
    this.stats = null;
    this.assistedStats = null;
    this.inputKeywordPerformance = null;
    this.currentRunRecordId = '';

    try {
      const response = await this.competitorFinderService.runCompetitorFinder({
        domain: normalizedDomain,
        limit: this.limit,
        forceRefresh,
        maxDomainSitemapPages: this.maxDomainSitemapPages,
        assistedTopicsLimit: this.assistedTopicsLimit,
        customAssistedTopics: this.selectedAssistedTopics,
        runId
      }).toPromise();

      if (!response?.success || !response.data) {
        this.error = 'Failed to run competitor finder.';
        return;
      }

      this.applyRunResponse(response);
      await this.loadSavedRuns(normalizedDomain);
    } catch (err: any) {
      this.error = err?.error?.message || err?.message || 'Failed to run competitor finder.';
    } finally {
      this.loading = false;
      this.finishRunProgress();
    }
  }

  async previewDiscovery(): Promise<void> {
    const normalizedDomain = this.normalizeDomain(this.domain);
    if (!normalizedDomain) return;
    this.previewLoading = true;
    try {
      const response = await this.competitorFinderService.previewCompetitorFinder({
        domain: normalizedDomain,
        limit: this.limit,
        assistedTopicsLimit: this.assistedTopicsLimit,
        customAssistedTopics: this.selectedAssistedTopics
      }).toPromise();
      if (response?.success && response.data) {
        this.assistedAvailability = response.data.pageAnalysisAssistedAvailable;
        this.searchPlanKeywords = response.data.searchPlan?.keywords || [];
        this.searchPlanTopics = response.data.searchPlan?.topicsUsed || [];
        this.hydrateTopicPools(response.data.topicPool || [], this.searchPlanTopics);
        this.estimatedCosts = response.data.estimatedCosts || null;
      }
    } catch {
      this.assistedAvailability = null;
      this.searchPlanKeywords = [];
      this.searchPlanTopics = [];
      this.selectedAssistedTopics = [];
      this.availableAssistedTopics = [];
      this.estimatedCosts = null;
      this.inputKeywordPerformance = null;
    } finally {
      this.previewLoading = false;
    }
  }

  private startRunProgress(runId: string): void {
    if (this.runProgressInterval) clearInterval(this.runProgressInterval);
    if (this.runBackendProgressInterval) clearInterval(this.runBackendProgressInterval);
    this.runProgress = 6;
    this.runProgressLabel = 'Finding competitors...';
    this.runProgressDetail = '';
    this.runStartedAt = Date.now();
    this.runProgressInterval = setInterval(() => {
      if (this.runProgress < 35) this.runProgress += 4;
      else if (this.runProgress < 70) this.runProgress += 2;
      else if (this.runProgress < 92) this.runProgress += 1;
      else if (this.runProgress < 99) this.runProgress += 0.2;

      if (this.runProgress < 35) {
        this.runProgressLabel = 'Finding competitors...';
        this.runProgressDetail = '';
      } else if (this.runProgress < 70) {
        this.runProgressLabel = 'Checking sitemap sizes...';
        this.runProgressDetail = 'This can be slow on very large sitemap indexes.';
      } else {
        this.runProgressLabel = 'Scoring and enriching results...';
        this.runProgressDetail = this.getElapsedDetail();
      }
    }, 500);

    this.pollRunProgress(runId);
    this.runBackendProgressInterval = setInterval(() => {
      this.pollRunProgress(runId);
    }, 1000);
  }

  private finishRunProgress(): void {
    if (this.runProgressInterval) {
      clearInterval(this.runProgressInterval);
      this.runProgressInterval = null;
    }
    if (this.runBackendProgressInterval) {
      clearInterval(this.runBackendProgressInterval);
      this.runBackendProgressInterval = null;
    }
    this.activeRunId = null;
    this.runProgress = 100;
    this.runProgressLabel = 'Done';
    this.runProgressDetail = '';
    this.runStartedAt = null;
    setTimeout(() => {
      if (!this.loading) {
        this.runProgress = 0;
        this.runProgressLabel = '';
        this.runProgressDetail = '';
      }
    }, 500);
  }

  private async pollRunProgress(runId: string): Promise<void> {
    if (!this.loading || this.activeRunId !== runId) return;
    try {
      const response = await this.competitorFinderService.getCompetitorFinderProgress(runId).toPromise();
      const progress = response?.data;
      if (!progress) return;

      this.runProgress = Math.max(this.runProgress, Math.min(100, progress.percent || 0));
      this.runProgressLabel = progress.message || this.runProgressLabel;
      if (progress.status === 'error') {
        this.runProgressDetail = progress.error || 'Run failed.';
      } else {
        this.runProgressDetail = this.getElapsedDetail();
      }
      if (progress.status === 'done') {
        this.runProgress = 100;
        this.runProgressLabel = progress.message || 'Done';
      }
    } catch {
      // Keep fallback progress behavior if progress endpoint is temporarily unavailable.
    }
  }

  private getElapsedDetail(): string {
    if (!this.runStartedAt) return '';
    const elapsedSec = Math.floor((Date.now() - this.runStartedAt) / 1000);
    if (elapsedSec < 10) return '';
    return `Still running (${elapsedSec}s). Server is responsive; waiting on external sitemap/SEO APIs.`;
  }

  async loadSavedRuns(domainFilter?: string): Promise<void> {
    this.savedRunsLoading = true;
    try {
      const normalizedDomain = this.normalizeDomain(domainFilter ?? this.domain);
      const response = await this.competitorFinderService.getCompetitorFinderRuns({
        domain: normalizedDomain || undefined,
        limit: 50
      }).toPromise();
      this.savedRuns = response?.data?.runs || [];
      if (!this.savedRuns.find(run => run.id === this.selectedSavedRunId)) {
        this.selectedSavedRunId = this.savedRuns[0]?.id || '';
      }
    } catch {
      this.savedRuns = [];
      this.selectedSavedRunId = '';
    } finally {
      this.savedRunsLoading = false;
    }
  }

  async loadSelectedRun(): Promise<void> {
    if (!this.selectedSavedRunId) return;
    this.loadingSavedRun = true;
    this.error = null;
    try {
      const response = await this.competitorFinderService.getCompetitorFinderRun(this.selectedSavedRunId).toPromise();
      if (!response?.success || !response.data) {
        this.error = 'Failed to load saved run.';
        return;
      }
      this.applyRunResponse(response);
      await this.loadSavedRuns(this.domain);
    } catch (err: any) {
      this.error = err?.error?.message || err?.message || 'Failed to load saved run.';
    } finally {
      this.loadingSavedRun = false;
    }
  }

  getSavedRunLabel(run: CompetitorFinderRunListItem): string {
    const stamp = this.formatDate(run.lastUpdated);
    return `${run.domain} - ${stamp} (${run.rowCount} rows, ${this.formatCurrency(run.totalCost)})`;
  }

  private applyRunResponse(response: CompetitorFinderResponse): void {
    const resolvedDomain = this.normalizeDomain(response?.data?.domain || this.domain);
    this.domain = resolvedDomain || this.domain;
    this.rows = response.data.rows || [];
    this.assistedRows = response.data.assistedRows || [];
    this.costBreakdown = response.data.costBreakdown || null;
    this.stats = response.data.stats || null;
    this.assistedStats = response.data.assistedStats || null;
    this.metadata = {
      dataSource: response.metadata?.dataSource || null,
      lastUpdated: response.metadata?.lastUpdated || null,
      maxDomainSitemapPages: response.metadata?.maxDomainSitemapPages ?? null,
      pageAnalysisAssistedAvailable: response.metadata?.pageAnalysisAssistedAvailable ?? null,
      cacheMissReason: response.metadata?.cacheMissReason || null
    };
    this.assistedAvailability = response.metadata?.pageAnalysisAssistedAvailable ?? null;
    this.searchPlanKeywords = response.metadata?.searchPlan?.keywords || [];
    this.searchPlanTopics = response.metadata?.searchPlan?.topicsUsed || [];
    this.hydrateTopicPools(response.metadata?.topicPool || [], this.searchPlanTopics);
    this.estimatedCosts = response.metadata?.estimatedCosts || null;
    this.inputKeywordPerformance = response.metadata?.inputKeywordPerformance || null;
    this.currentRunRecordId = response.metadata?.runRecordId || '';

    this.rows.forEach(row => {
      this.loadCrawlStatus(row.domain);
    });
    this.assistedRows.forEach(row => {
      this.loadCrawlStatus(row.domain);
    });
  }

  private hydrateTopicPools(topicPool: string[], selectedFromServer: string[]): void {
    const uniquePool = Array.from(new Set((topicPool || []).map(topic => (topic || '').trim().toLowerCase()).filter(Boolean)));
    const selected = Array.from(new Set(
      (this.selectedAssistedTopics.length ? this.selectedAssistedTopics : selectedFromServer)
        .map(topic => (topic || '').trim().toLowerCase())
        .filter(Boolean)
    ));
    this.selectedAssistedTopics = selected.slice(0, 30);
    this.availableAssistedTopics = uniquePool
      .filter(topic => !this.selectedAssistedTopics.includes(topic))
      .slice(0, 20);
    this.pendingSwapTopic = null;
  }

  addTopicToSelection(topic: string): void {
    const clean = (topic || '').trim().toLowerCase();
    if (!clean || this.selectedAssistedTopics.includes(clean)) return;
    this.selectedAssistedTopics = [...this.selectedAssistedTopics, clean].slice(0, 30);
    this.availableAssistedTopics = this.availableAssistedTopics.filter(item => item !== clean);
    this.pendingSwapTopic = null;
    this.previewDiscovery();
  }

  removeTopicFromSelection(topic: string): void {
    const clean = (topic || '').trim().toLowerCase();
    this.selectedAssistedTopics = this.selectedAssistedTopics.filter(item => item !== clean);
    if (clean && !this.availableAssistedTopics.includes(clean)) {
      this.availableAssistedTopics = [clean, ...this.availableAssistedTopics].slice(0, 20);
    }
    this.pendingSwapTopic = null;
    this.previewDiscovery();
  }

  onTopicChipClick(topic: string, source: 'selected' | 'available'): void {
    const clean = (topic || '').trim().toLowerCase();
    if (!clean) return;

    if (!this.pendingSwapTopic) {
      this.pendingSwapTopic = { topic: clean, source };
      return;
    }

    if (this.pendingSwapTopic.source === source) {
      this.pendingSwapTopic = this.pendingSwapTopic.topic === clean
        ? null
        : { topic: clean, source };
      return;
    }

    const selectedTopic = source === 'selected' ? clean : this.pendingSwapTopic.topic;
    const availableTopic = source === 'available' ? clean : this.pendingSwapTopic.topic;
    this.swapTopics(selectedTopic, availableTopic);
  }

  isPendingSwap(topic: string, source: 'selected' | 'available'): boolean {
    const clean = (topic || '').trim().toLowerCase();
    return !!this.pendingSwapTopic && this.pendingSwapTopic.topic === clean && this.pendingSwapTopic.source === source;
  }

  private swapTopics(selectedTopic: string, availableTopic: string): void {
    const selectedIdx = this.selectedAssistedTopics.indexOf(selectedTopic);
    const availableIdx = this.availableAssistedTopics.indexOf(availableTopic);
    if (selectedIdx < 0 || availableIdx < 0) {
      this.pendingSwapTopic = null;
      return;
    }

    const nextSelected = [...this.selectedAssistedTopics];
    const nextAvailable = [...this.availableAssistedTopics];
    nextSelected[selectedIdx] = availableTopic;
    nextAvailable[availableIdx] = selectedTopic;
    this.selectedAssistedTopics = Array.from(new Set(nextSelected)).slice(0, 30);
    this.availableAssistedTopics = Array.from(new Set(nextAvailable)).slice(0, 20);
    this.pendingSwapTopic = null;
    this.previewDiscovery();
  }

  onTopicDragStart(topic: string): void {
    this.draggedTopic = topic;
  }

  onTopicDropToSelected(event: DragEvent): void {
    event.preventDefault();
    if (this.draggedTopic) this.addTopicToSelection(this.draggedTopic);
    this.draggedTopic = null;
  }

  onTopicDropToAvailable(event: DragEvent): void {
    event.preventDefault();
    if (this.draggedTopic) this.removeTopicFromSelection(this.draggedTopic);
    this.draggedTopic = null;
  }

  allowTopicDrop(event: DragEvent): void {
    event.preventDefault();
  }

  async estimateCrawl(domain: string): Promise<void> {
    this.crawlEstimateLoading.set(domain, true);
    this.crawlEstimateError.set(domain, null);

    try {
      const response = await this.crawlService.estimateCrawl(domain).toPromise();
      if (response?.success && response.data) {
        this.crawlEstimates.set(domain, response.data);
      } else {
        this.crawlEstimateError.set(domain, 'Failed to estimate crawl.');
      }
    } catch (err: any) {
      this.crawlEstimateError.set(domain, err?.error?.message || err?.message || 'Failed to estimate crawl.');
    } finally {
      this.crawlEstimateLoading.set(domain, false);
    }
  }

  async startCrawl(domain: string): Promise<void> {
    this.crawlStarting.set(domain, true);
    this.crawlStatusError.set(domain, null);

    try {
      await this.crawlService.startCrawl(domain, {
        // Match Page Comparison/Crawl Management behavior:
        // always request the standard full crawl cap.
        limit: 750,
        priority: 'high',
        frequency: 'manual',
        enableBrowserRendering: false
      }).toPromise();
      this.loadCrawlStatus(domain);
      this.pollCrawlStatus(domain);
    } catch (err: any) {
      this.crawlStatusError.set(domain, err?.error?.message || err?.message || 'Failed to start crawl.');
    } finally {
      this.crawlStarting.set(domain, false);
    }
  }

  async loadCrawlStatus(domain: string): Promise<void> {
    this.crawlStatusLoading.set(domain, true);
    this.crawlStatusError.set(domain, null);

    try {
      const status = await this.crawlService.getCrawlStatus(domain).toPromise();
      if (status) {
        this.crawlStatuses.set(domain, status);
      }
    } catch (err: any) {
      this.crawlStatusError.set(domain, err?.error?.message || err?.message || 'Failed to load crawl status.');
    } finally {
      this.crawlStatusLoading.set(domain, false);
    }
  }

  private pollCrawlStatus(domain: string): void {
    const existing = this.crawlPollingIntervals.get(domain);
    if (existing) {
      clearInterval(existing);
      this.crawlPollingIntervals.delete(domain);
    }

    const interval = setInterval(async () => {
      await this.loadCrawlStatus(domain);
      const status = this.crawlStatuses.get(domain);
      if (!status) return;

      const isActive = status.phase === 'crawling' || status.phase === 'analyzing' || status.pagesInQueue > 0;
      if (!isActive) {
        const timer = this.crawlPollingIntervals.get(domain);
        if (timer) {
          clearInterval(timer);
          this.crawlPollingIntervals.delete(domain);
        }
      }
    }, 5000);

    this.crawlPollingIntervals.set(domain, interval);
  }

  formatDate(value: string | null): string {
    if (!value) return 'N/A';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  }

  formatCurrency(value: number | null | undefined): string {
    if (value == null) return '$0.00';
    return `$${value.toFixed(4)}`;
  }

  getCrawlStatus(domain: string): CrawlStatus | null {
    return this.crawlStatuses.get(domain) || null;
  }

  getCrawlStatusLabel(domain: string): string {
    const status = this.getCrawlStatus(domain);
    if (!status) return 'No crawl';
    if (status.phase === 'crawling') return 'Crawling';
    if (status.phase === 'analyzing') return 'Analyzing';
    if (status.phase === 'complete') return 'Complete';
    return 'Idle';
  }

  canRemoveRow(domain: string): boolean {
    const status = this.getCrawlStatus(domain);
    if (!status) return true;
    if (status.phase === 'complete' || status.phase === 'crawling' || status.phase === 'analyzing') return false;
    if ((status.pagesCrawled || 0) > 0) return false;
    if ((status.pagesInQueue || 0) > 0) return false;
    return true;
  }

  isRemovingRow(domain: string, listType: 'rows' | 'assistedRows'): boolean {
    return this.removingRows.get(`${listType}:${domain}`) || false;
  }

  async removeRow(domain: string, listType: 'rows' | 'assistedRows'): Promise<void> {
    if (!this.currentRunRecordId) {
      this.error = 'Save or open a run before removing rows.';
      return;
    }
    if (!this.canRemoveRow(domain)) {
      this.error = `Cannot remove ${domain}: crawl has already started or completed.`;
      return;
    }

    const key = `${listType}:${domain}`;
    this.removingRows.set(key, true);
    this.error = null;
    try {
      await this.competitorFinderService.removeCompetitorFinderRow(this.currentRunRecordId, domain, listType).toPromise();
      if (listType === 'assistedRows') {
        this.assistedRows = this.assistedRows.filter(row => this.normalizeDomain(row.domain) !== this.normalizeDomain(domain));
      } else {
        this.rows = this.rows.filter(row => this.normalizeDomain(row.domain) !== this.normalizeDomain(domain));
      }
    } catch (err: any) {
      this.error = err?.error?.message || err?.message || 'Failed to remove row.';
    } finally {
      this.removingRows.set(key, false);
    }
  }

  getDomainHomepageUrl(domain: string): string {
    const normalized = this.normalizeDomain(domain || '');
    if (!normalized) return '#';
    return `https://${normalized}`;
  }

  hasIntersectionDetails(row: CompetitorFinderRow): boolean {
    const bands = row.intersectionBandCounts;
    const hasBandCounts = !!bands && ((bands.bandA + bands.bandB + bands.bandC + bands.bandD) > 0);
    const hasSamples = (row.intersectionSamples?.length || 0) > 0;
    return hasBandCounts || hasSamples;
  }
}
